import {
  BookingCalendarSteps,
  EventTimeZoneTypeEnum,
} from 'common/components/entities/Calendar/constants'
import { CommonBookingPaidCalendar } from 'common/components/entities/Calendar/paid-calendar'
import { BookingPaidCalendarInterface } from 'common/types/entities/booking-paid-calendar-interface'
import ChildrenEntities from 'publisher/components/ChildrenEntities'
import StyleWrapper from 'publisher/components/core/StyleWrapper'
import { managementSelectors, useManagement } from 'publisher/store'
import React, { useRef } from 'react'
import { scrollElementTopIntoViewIfTopIntoView } from 'src/publisher/utils/scroll-element-top-into-view-if-top-into-view'
import { PaidCalendarPaymentButton } from './payment-buttons/paid-calendar-payment-button'
import { useBookingPaidCalendarParams } from './use-booking-paid-calendar-params'
import { usePaidCalendarErrors } from './use-paid-calendar-step-errors'

interface BookingCalendarProps {
  entity: BookingPaidCalendarInterface
}

export const BookingPaidCalendar = ({ entity }: BookingCalendarProps) => {
  const calendarRef = useRef<HTMLDivElement>(null)
  const isDesktop = useManagement(managementSelectors.isDesktop)
  const {
    event,
    timeSlots,
    isEventFetching,
    isTimeSlotsFetching,
    activeStep,
    setActiveStep,
    selectedDate,
    selectedTimeZone,
    selectedTimeSlot,
    selectedLocationId,
    handleChangeField,
    onMonthSelect,
    globalFontFamily,
    globalMobileFontSize,
    globalFontSize,
    actualEventError,
  } = useBookingPaidCalendarParams(entity)

  const commonStyles = {
    color: entity.color,
    mobileColor: entity.mobileColor,
    mobilePadding: entity.mobilePadding,
    mobileMargin: entity.mobileMargin,
    fontFamily: globalFontFamily,
    fontSize: globalFontSize,
    mobileFontFamily: globalFontFamily,
    mobileFontSize: globalMobileFontSize,
  }

  const {
    errors: calendarStepErrors,
    isErrorsVisible,
    setIsErrorsVisible,
  } = usePaidCalendarErrors()

  return (
    <StyleWrapper
      mobileMargin={entity.mobileMargin}
      margin={entity.margin}
      appearance={entity.appearance}
    >
      <CommonBookingPaidCalendar
        ref={calendarRef}
        entity={entity}
        selectedDate={selectedDate}
        onDateSelect={date => {
          handleChangeField('date', date)
          handleChangeField('timeSlot', '')
        }}
        selectedTimeSlot={selectedTimeSlot}
        onTimeSlotSelect={timeSlot => handleChangeField('timeSlot', timeSlot)}
        selectedTimeZone={selectedTimeZone}
        onTimeZoneSelect={tz => {
          if (event?.displayTimezoneType !== EventTimeZoneTypeEnum.Fixed) {
            handleChangeField('timeZone', tz)
            handleChangeField('date', '')
            handleChangeField('timeSlot', '')
          }
        }}
        selectedLocationId={selectedLocationId}
        onLocationSelect={locationId => {
          handleChangeField('eventLocationId', locationId)
        }}
        onMonthSelect={onMonthSelect}
        onNextStepClick={e => {
          e?.preventDefault()
          if (!calendarStepErrors.length) {
            setActiveStep(BookingCalendarSteps.FormStep)
            scrollElementTopIntoViewIfTopIntoView(calendarRef.current)
          } else if (!isErrorsVisible) {
            setIsErrorsVisible(true)
          }
        }}
        calendarStepErrors={
          actualEventError
            ? [actualEventError]
            : isErrorsVisible
              ? [...calendarStepErrors]
              : []
        }
        onPrevStepClick={() => {
          setActiveStep(BookingCalendarSteps.CalendarStep)
        }}
        activeStep={activeStep}
        availabilitySlots={timeSlots}
        event={event}
        fontSize={globalFontSize}
        mobileFontSize={globalMobileFontSize}
        fontFamily={globalFontFamily}
        mobileFontFamily={globalFontFamily}
        isLoading={isEventFetching || isTimeSlotsFetching}
        formStepChildren={<ChildrenEntities childIds={entity.childIds} />}
        formStepButton={
          <PaidCalendarPaymentButton
            entity={entity}
            commonStyles={commonStyles}
          />
        }
        isMobile={!isDesktop}
      />
    </StyleWrapper>
  )
}
